import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * Booking auth gate.
 *
 * Returns an onClick handler that intercepts any outbound booking link
 * (Pitchup, Campsites.co.uk, direct website, tel: phone) when the user
 * is NOT signed in. They're redirected to /register with a friendly
 * toast and a `next` query param remembering exactly where to send them
 * back to. After signup, they land on the same campsite page they were
 * about to book — one tap and they're back on the booking link.
 *
 * Why we gate booking: tracking who books what is how we measure value
 * and prove affiliate referrals to AWIN/Pitchup. Unauthenticated taps
 * are commission leakage. Also lets us send confirmation emails, weather
 * alerts, etc. The free non-Pro tier is genuinely free; signup is the
 * 20-second hurdle.
 *
 * Usage:
 *   const gate = useBookingGate();
 *   <a href={url} onClick={(e) => gate(e, { reason: "Pitchup" })}>Book</a>
 */
export function useBookingGate() {
  const { user } = useAuth();
  const nav = useNavigate();

  return useCallback((e, { reason = "book this site" } = {}) => {
    if (user) return;  // signed in → let the click through
    if (e) e.preventDefault();
    const next = `${window.location.pathname}${window.location.search}`;
    toast.info(`Sign up to ${reason} — takes 20 seconds`, { duration: 4000 });
    nav(`/register?next=${encodeURIComponent(next)}`);
  }, [user, nav]);
}
